import { UserState } from '@/composables/User';
import { fetchShopConfigurationsNest } from '@/composables/shop/ShopsConfiguration';
import { isDemoModeActive, demoOverrides } from '@/composables/DemoMode';

/** Regex to detect a variable expression like {var=...} */
const VAR_PATTERN = /^\{var=.+\}$/;

/** Variable prefixes that can be resolved client-side (others are server-side only) */
const CLIENT_RESOLVABLE_PREFIXES = ['shop.'];

/** The data attribute name used to store raw variable expressions */
export const DATA_VAR_SRC = 'data-var-src';

/** Default placeholder image URL */
const PLACEHOLDER_URL = 'http://media.shopimind.io/img/v4/placeholders/image_placeholder.png';

/** Module-level state for push notification default icon URL */
let pushDefaultIconUrl = '';

/**
 * Set the push notification default icon URL (called from config page or template editor init).
 */
export const setPushDefaultIconUrl = (url: string) => {
  pushDefaultIconUrl = url;
};

/**
 * Load the push notification default icon URL from shops_configuration API.
 */
export const initPushDefaultIconUrl = async (shopId: number): Promise<void> => {
  try {
    const data = await fetchShopConfigurationsNest(shopId, ['shopRequiredDataPushNotification_defaultIconPath']);
    if (data?.shopRequiredDataPushNotification_defaultIconPath) {
      pushDefaultIconUrl = data.shopRequiredDataPushNotification_defaultIconPath;
    }
  } catch (e) {
    // Silently fail - variable just won't resolve
  }
};

/**
 * Returns true if the given string is a template variable expression.
 */
export const isVariableExpression = (value: string): boolean => VAR_PATTERN.test(value.trim());

/**
 * Returns true if the variable expression can be resolved client-side.
 * Variables like {var=product.*} are server-side only and must be left untouched.
 */
export const isClientResolvableVariable = (value: string): boolean => {
  const match = value.match(/\{var="?([^\s"}]+)"?/);
  if (!match) return false;
  return CLIENT_RESOLVABLE_PREFIXES.some((prefix) => match[1].startsWith(prefix));
};

/**
 * Build an enriched shop context with derived properties that the backend normally provides.
 * For example, logo_url is constructed from the logo filename.
 */
const buildShopContext = (): Record<string, any> => {
  const shop: Record<string, any> = { ...UserState.activeShop };
  // The backend provides logo_url as a full URL; construct it from the logo filename for preview
  if (shop.logo && !shop.logo_url) {
    shop.logo_url = `https://media.shopimind.io/clients/logos/${shop.logo}`;
  }
  // Push notification default icon URL from shops_configuration
  if (pushDefaultIconUrl) {
    shop.push_icon_url = pushDefaultIconUrl;
  }
  // Apply demo overrides when demo mode is active
  if (isDemoModeActive.value) {
    if (demoOverrides.name) shop.name = demoOverrides.name;
    if (demoOverrides.url) shop.url = demoOverrides.url;
    if (demoOverrides.logo_url) shop.logo_url = demoOverrides.logo_url;
    if (demoOverrides.push_icon_url) shop.push_icon_url = demoOverrides.push_icon_url;
  }
  return shop;
};

/**
 * Synchronously resolve a single variable expression against the current shop context.
 * For a variable like {var=shop.logo_url}, walks context.shop.logo_url.
 * Returns the resolved value string, or '' if not resolvable.
 */
export const resolveVariableSync = (varExpression: string): string => {
  const match = varExpression.match(/\{var="?([^\s"}]+)"?/);
  if (!match) return '';

  const variablePath = match[1];
  const context: Record<string, any> = {
    shop: buildShopContext(),
  };

  const parts = variablePath.split('.');
  let current: any = context;
  for (const part of parts) {
    if (current && typeof current === 'object' && part in current) {
      current = current[part];
    } else {
      return '';
    }
  }

  return typeof current === 'string' ? current : '';
};

/**
 * Apply a variable expression to an img element in the DOM:
 * 1. Store the raw variable in data-var-src
 * 2. Resolve and set the actual URL in src for preview display
 * Returns the resolved URL (or '' if unresolvable).
 */
export const applyVariableToImage = (element: HTMLElement, varExpression: string): string => {
  element.setAttribute(DATA_VAR_SRC, varExpression);
  const resolvedUrl = resolveVariableSync(varExpression);
  if (resolvedUrl) {
    element.setAttribute('src', resolvedUrl);
  } else {
    element.setAttribute('src', PLACEHOLDER_URL);
  }
  return resolvedUrl;
};

/**
 * On template load: scan all img elements for {var=...} in their src attribute.
 * For each found, move the variable to data-var-src and resolve the src for display.
 */
export const processVariableImagesOnLoad = (document: Document | HTMLElement): void => {
  const images = document.querySelectorAll('img[src]');
  images.forEach((img) => {
    const src = img.getAttribute('src') || '';
    if (isVariableExpression(src) && isClientResolvableVariable(src)) {
      applyVariableToImage(img as HTMLElement, src);
    }
  });
};

/**
 * Restore variable expressions into src attributes before saving.
 * Operates on the provided element (should be a clone to avoid affecting live preview).
 * For all elements with data-var-src: sets src to the raw variable and removes data-var-src.
 */
export const restoreVariablesInElement = (element: HTMLElement): void => {
  const varImages = element.querySelectorAll(`[${DATA_VAR_SRC}]`);
  varImages.forEach((img) => {
    const rawVar = img.getAttribute(DATA_VAR_SRC) || '';
    if (rawVar) {
      img.setAttribute('src', rawVar);
      img.removeAttribute(DATA_VAR_SRC);
    }
  });
};

/**
 * Get a placeholder image URL for when a variable cannot be resolved.
 */
export const getVariablePlaceholderUrl = (): string => PLACEHOLDER_URL;
